import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useVoice } from '../context/VoiceContext';

export default function Medicines() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const { speak } = useVoice();

  const load = () => {
    api.getMedicines()
      .then(data => { setMedicines(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个药品吗？相关的提醒也会被删除。')) return;
    try {
      await api.deleteMedicine(id);
      speak('药品已删除');
      load();
    } catch (err) {
      alert('删除失败：' + err.message);
    }
  };

  /**
   * 暂停 / 恢复。
   *
   * 暂停不是「删掉」也不是「不提醒了」，而是临时停一段时间 ——
   * 住院、换药、医生让先停几天，都会用到。暂停后今天剩下的提醒不再响，
   * 恢复时也不会把已经过去的时间点补回来（否则会凭空多出几次漏服）。
   */
  const handleToggleStatus = async (med) => {
    const next = med.status === 'paused' ? 'active' : 'paused';
    if (next === 'paused' && !window.confirm(`暂停后「${med.name}」今天剩下的提醒不会再响，确定暂停吗？`)) return;
    try {
      await api.updateMedicineStatus(med.id, next);
      speak(next === 'paused' ? `${med.name}已暂停提醒` : `${med.name}已恢复提醒`);
      load();
    } catch (err) {
      alert('操作失败：' + err.message);
    }
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="app-container">
      {/* 顺序：标题 → 添加按钮条 → 列表（用户指定的最终形态）。
          中间试过把按钮条提到标题**之上**，当时的理由是「吸顶条要不留缝隙地贴住屏幕顶边」。
          但那个做法把页面的「这是什么页」先干掉了：第一屏最上面是一个操作按钮，
          而不是页面名字，进来的人（尤其第一次用）会先愣一下。
          真正的目标其实是「下拉时按钮条还在」——那是 sticky 的职责，跟谁排在前头无关：
          现在标题在上、按钮条在下，滚动时标题正常滚走，按钮条贴住顶部，
          两件事同时成立。条上的全出血背景（见 global.css .add-medicine-bar）保证
          卡片不会从两侧 16px 的空白里透出来。 */}
      <h1 className="page-title">药品管理</h1>

      <div className="add-medicine-bar">
        <Link to="/medicines/add">
          <button className="btn-primary btn-large">
            ➕ 添加药品
          </button>
        </Link>
      </div>

      {medicines.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">💊</div>
            <p>还没有添加药品呢</p>
            <p style={{ marginTop: 8, fontSize: 'var(--font-size-small)' }}>
              点上面的按钮，加第一种药吧
            </p>
          </div>
        </div>
      ) : (
        medicines.map(med => (
          <div key={med.id} className={`card med-card ${med.status === 'paused' ? 'paused' : ''}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 'var(--font-size-large)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {med.name}
                  <span className={`tag ${med.status === 'paused' ? 'tag-warning' : 'tag-success'}`}>
                    {med.status === 'paused' ? '已暂停' : '服用中'}
                  </span>
                </h2>
                {/* 剂量 + 频次 + 时间点原来占两行（「每次 1 片」一行、
                    「每日2次 · 08:00、18:00」一行）。合成一行后卡片矮一截，
                    药品多的时候一屏能多看见一种药；超长时仍会自然折行，
                    只是折点从固定的一行变成按宽度决定。 */}
                <p style={{ color: 'var(--text-secondary)', marginBottom: 4, lineHeight: 1.5 }}>
                  每次 {med.dosage} {med.unit || '片'}
                  <span style={{ color: 'var(--text-light)' }}> · </span>
                  {med.frequency}
                  {(() => {
                    let times = [];
                    try { times = JSON.parse(med.times); } catch (e) { times = []; }
                    return Array.isArray(times) && times.length > 0
                      ? <><span style={{ color: 'var(--text-light)' }}> · </span>{times.join('、')}</>
                      : null;
                  })()}
                </p>
                {med.status === 'paused' && (
                  <p style={{ color: 'var(--warning-color)', fontSize: 'var(--font-size-small)', marginTop: 6, lineHeight: 1.5 }}>
                    已暂停提醒，恢复后从下一个时间点继续
                  </p>
                )}
                {med.notes && (
                  <p style={{ color: 'var(--warning-color)', fontSize: 'var(--font-size-small)', marginTop: 6, lineHeight: 1.5 }}>
                    注意：{med.notes}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Link to={`/medicines/edit/${med.id}`} style={{ textDecoration: 'none' }}>
                  <button
                    className="btn-secondary"
                    style={{ width: '100%', padding: '8px 16px', minHeight: 40, fontSize: 'var(--font-size-small)' }}
                  >
                    编辑
                  </button>
                </Link>
                <button
                  className="btn-outline"
                  style={{ width: '100%', padding: '8px 16px', minHeight: 40, fontSize: 'var(--font-size-small)' }}
                  onClick={() => handleToggleStatus(med)}
                >
                  {med.status === 'paused' ? '恢复' : '暂停'}
                </button>
                <button
                  className="btn-danger"
                  style={{ width: '100%', padding: '8px 16px', minHeight: 40, fontSize: 'var(--font-size-small)' }}
                  onClick={() => handleDelete(med.id)}
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}